import { Context } from '@nuxt/types'
import ROUTES from '~/config/routes'
import { ROUTE_PATHS } from '~/config/routePaths'
import { Shops } from '~/config/shops'
import { NuxtRoute, Route } from '~/config/routes.types'

const isNuxtRouter = (route: Route | null | undefined): route is NuxtRoute => {
  return (route as NuxtRoute)?.component !== undefined
}

type RoutesTypes = keyof typeof ROUTES

/**
 * If the current URL matches an alias path instead of the shop-specific primary
 * path, issue a 301 redirect to the canonical (localized) primary path.
 *
 * Example: /personal-data-forms/verify/ABC → /urejanje-osebnih-podatkov/potrditev/ABC (SI)
 *
 * Returns true when a redirect was issued (caller should return immediately).
 */
function redirectAliasIfNeeded(context: Context): boolean {
  const routeName: any = context?.route?.name
  const shopId = context.app.$shopConfig?.shopId as Shops | undefined
  if (!routeName || !shopId) return false

  const routePathConfig = Object.values(ROUTE_PATHS).find(
    (r) => r.name === routeName
  )
  if (!routePathConfig?.aliases) return false

  const aliasPattern = routePathConfig.aliases[shopId]
  const primaryPattern = routePathConfig.paths[shopId]
  if (!aliasPattern || !primaryPattern) return false

  // Derive the static base of the alias (everything before the first dynamic segment)
  const aliasBase = '/' + aliasPattern.replace(/:[^/]+.*$/, '')
  if (!context.route.path.startsWith(aliasBase)) return false

  // Build redirect target by swapping the alias base for the primary base
  const primaryBase = '/' + primaryPattern.replace(/:[^/]+.*$/, '')
  const redirectPath = context.route.path.replace(aliasBase, primaryBase)

  context.redirect(301, redirectPath, context.route.query)
  return true
}

export default function (context: Context) {
  if (process.server) {
    // On server, verify the matched route has a Nuxt component.
    // Routes without a component are handled by an external presenter system
    // and should never be SSR-rendered by Nuxt.
    const routeName: any = context?.route?.name
    const route = ROUTES[routeName as RoutesTypes]

    if (redirectAliasIfNeeded(context)) return

    if (routeName && !isNuxtRouter(route)) {
      context.error({
        statusCode: 404,
        message: 'This page could not be found',
      })
    }
    return
  }

  if (window.location.href.includes('0.0.0.0')) {
    return
  }

  const shopId = context.app.$shopConfig?.shopId

  if (!shopId) {
    throw new Error(
      'middleware/routing: Shop id must be provided in shopConfig'
    )
  }

  if (redirectAliasIfNeeded(context)) return

  const routeName: any = context?.route?.name
  const route = ROUTES[routeName as RoutesTypes]

  if (!isNuxtRouter(route)) {
    // Force a full page reload so the external presenter system (monolith/proxy)
    // can handle the route instead of the SPA router.
    // Return a never-resolving Promise to prevent Nuxt from rendering the 404 error page
    // while the browser performs the redirect.
    window.location.href = context.route.fullPath
    return new Promise(() => {})
  }
}
